import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { chakra, Box, BoxProps, Tooltip } from '@chakra-ui/react';
import { DataObjectStatus } from '@netiva/classifieds-api';

import { getFilterStatus } from '@/pages/MyAds/utils';

export type StatusBadgeProps = BoxProps & {
  status: DataObjectStatus;
  hasFutureStart: boolean;
};

export const StatusBadge: FC<StatusBadgeProps> = ({ status, hasFutureStart, ...rest }) => {
  const { t } = useTranslation();
  // pending changes read as "in review", the same as an advert waiting for its first approval
  const displayStatus = status === 'ToApproveChanges' ? 'ToApprove' : status;
  const statusName = displayStatus.toString().toLowerCase();
  // only an ad that is still going online can be planned - a stopped one keeps the start date of its prolongation
  const isPlanned = hasFutureStart && getFilterStatus(status) === 'active';
  const label = isPlanned ? 'ad.status.planned' : `ad.status.${statusName}`;
  const color = useMemo(() => {
    switch (displayStatus) {
      case 'Active':
      case 'Approved':
        return isPlanned ? 'green.200' : 'green.400';
      case 'Draft':
        return 'yellow.400';
      case 'ToApprove':
        return 'blue.400';
      case 'Disabled':
        return 'gray.400';
      case 'Expired':
        return 'orange.400';
      default:
        return 'transparent';
    }
  }, [isPlanned, displayStatus]);

  return (
    <Box position="absolute" top={0} left={0} borderTopLeftRadius="default" overflow="hidden" {...rest}>
      <svg width="50" height="50" viewBox="0 0 100 100">
        <Tooltip label={t(label, { defaultValue: 'Unknown' })} placement="top">
          <chakra.path fill={color} d="M 0 0 L 100 0 L 0 100 Z" tabIndex={0} />
        </Tooltip>
      </svg>
    </Box>
  );
};
